/**
 * @file useAuth.js
 * @description Custom React hook that manages Supabase authentication state.
 *   Loads the initial session, subscribes to auth state changes, and exposes
 *   the session, user, profile, loading flag, setProfile, and signOut.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module hooks/useAuth
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { supabase, isRecoveryFlow } from "../lib/supabase";
import { trackEvent, EVENTS } from "../lib/eventService";
import { claimAnonEvents, getUtmContent } from "../lib/attribution";
import { track, A } from "../lib/analytics";
import { peekPendingCase, peekSignupIntent } from "../lib/signupIntent";

/**
 * @description Manages Supabase session and profile loading. Subscribes to auth
 *   state changes and keeps the profile row in sync with the current session.
 * @returns {{ session: Object|null, user: Object|null, profile: Object|null, loading: boolean, setProfile: Function, signOut: Function }}
 */
export function useAuth() {
  const [session, setSession]       = useState(null);
  const [profile, setProfile]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [recoveryMode, setRecoveryModeState] = useState(isRecoveryFlow);

  // Sticky recovery flag. Once a page load is identified as a password-reset
  // (either via the URL captured at module load, or the PASSWORD_RECOVERY auth
  // event), we must hold the user on the reset screen regardless of the order
  // or type of subsequent auth events (INITIAL_SESSION, SIGNED_IN, USER_UPDATED
  // all fire during this flow and would otherwise route the user away).
  const recoveryActiveRef = useRef(isRecoveryFlow);

  // Tracks whose profile is currently loaded. Supabase re-fires auth events
  // (TOKEN_REFRESHED / SIGNED_IN) whenever the tab regains focus; without this
  // guard every refocus would re-fetch the profile and churn downstream state,
  // causing a visible re-render. We only reload when the user actually changes.
  const loadedUserIdRef = useRef(null);

  // Load profile from DB for the current user
  async function loadProfile(userId) {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();
    if (!error && data) { setProfile(data); loadedUserIdRef.current = userId; }
    else setProfile(null);
  }

  useEffect(() => {
    // Get existing session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) loadProfile(session.user.id).finally(() => setLoading(false));
      else setLoading(false);
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "PASSWORD_RECOVERY") {
          recoveryActiveRef.current = true;
          setRecoveryModeState(true);
          setSession(session);
          setLoading(false);
          return;
        }

        if (event === "SIGNED_OUT") {
          recoveryActiveRef.current = false;
          setRecoveryModeState(false);
          setSession(null);
          setProfile(null);
          loadedUserIdRef.current = null;
          setLoading(false);
          return;
        }

        // While in recovery, keep the session/profile fresh but never route the
        // user away — exit happens only via exitRecovery() after they submit a
        // new password. INITIAL_SESSION / SIGNED_IN / USER_UPDATED all land here.
        if (recoveryActiveRef.current) {
          setSession(session);
          if (session?.user) loadProfile(session.user.id);
          setLoading(false);
          return;
        }

        setSession(session);
        if (session?.user) {
          // Only re-fetch when the user changed (real login / account switch).
          // Token refreshes and focus-driven SIGNED_IN events keep the same user
          // and must not trigger a redundant reload.
          if (session.user.id !== loadedUserIdRef.current) {
            // Audit event on a genuine sign-in (SIGNED_IN + new user) — never
            // on INITIAL_SESSION (reload while logged in) or refocus/refresh
            // for the same user (filtered by the id-change guard above).
            // A fresh signup also fires SIGNED_IN; AuthScreen sets a flag so we
            // record it as SIGN_UP (with role) instead of a redundant LOGIN.
            if (event === "SIGNED_IN") {
              let signingUp = false;
              try {
                signingUp = sessionStorage.getItem("aba:signup_in_progress") === "1";
                if (signingUp) sessionStorage.removeItem("aba:signup_in_progress");
              } catch { /* ignore */ }
              // Signup intent (set by the free-tracker / case-alert CTAs) → mark
              // the interest flag and fire the matching completion conversion.
              const intent = peekSignupIntent();
              const wantsTracker = intent === "tracker";
              const wantsAlerts  = intent === "alerts";

              if (signingUp) {
                // Writes the in-house sign_up row AND auto-mirrors a GA4-standard
                // `sign_up` event (with role) for EVERY signup — the mirror in
                // eventService handles the GA4 forward, so no separate gtag call
                // is needed here. This fires regardless of which CTA (or none)
                // set the intent flag, so GA4 sees all account creations.
                // utm_content (outreach prospect id) rides along when present so the
                // sign_up row ties to the exact prospect; GA4's mirror only keeps `role`.
                const utmContent = getUtmContent();
                // Signed up from a case (a guest tapped Apply on it): the case rides along, so Admin > Analytics can
                // count signups per case in the apply funnel.
                const fromCase = peekPendingCase();
                trackEvent(session.user.id, EVENTS.SIGN_UP, {
                  role: session.user.user_metadata?.role || null,
                  ...(utmContent ? { utm_content: utmContent } : {}),
                  ...(fromCase ? { case_id: fromCase.id, case_code: fromCase.code || null } : {}),
                });
                if (wantsTracker) track(A.TRACKER_SIGNUP_COMPLETED, { userId: session.user.id });
                if (wantsAlerts)  track(A.CASE_ALERT_SIGNUP_COMPLETED, { userId: session.user.id });
              } else {
                trackEvent(session.user.id, EVENTS.LOGIN, {});
              }

              // Link this browser's pre-signup guest events to the user and
              // stamp first-touch attribution (source of truth, Supabase only).
              claimAnonEvents(supabase, session.user.id, {
                interested_in_tracker:     wantsTracker,
                interested_in_case_alerts: wantsAlerts,
              });
            }
            loadProfile(session.user.id);
          }
        } else { setProfile(null); loadedUserIdRef.current = null; setLoading(false); }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Explicit exit from recovery mode (called once the new password is set).
  const exitRecovery = useCallback(() => {
    recoveryActiveRef.current = false;
    setRecoveryModeState(false);
  }, []);

  async function signOut() {
    recoveryActiveRef.current = false;
    // Capture the user id BEFORE signing out so the LOGOUT audit event can be
    // attributed. Fire-and-forget — must not block or fail the sign-out.
    const uid = session?.user?.id || loadedUserIdRef.current;
    if (uid) trackEvent(uid, EVENTS.LOGOUT, {});
    // scope: "local" only clears this device's session — avoids the
    // /logout?scope=global server call that 403s when the access token is
    // already expired/invalid. Wrapped so local state always clears regardless.
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch (_) { /* non-fatal — clear local state below either way */ }
    setSession(null);
    setProfile(null);
    setRecoveryModeState(false);
  }

  return {
    session,
    user: session?.user ?? null,
    profile,
    setProfile,
    loading,
    recoveryMode,
    setRecoveryMode: exitRecovery,
    signOut,
  };
}
